import type { DeliveryTimeOption, PromiseDateSettings } from '@/api/types/services';

import { defaultTimezoneForCountries } from './timezones';

/**
 * Transit-day ranges per delivery-time preset. Mirrors the backend
 * App\Domain\Carrier\Enums\DeliveryTime.
 */
export const DELIVERY_TIME_TRANSIT: Record<DeliveryTimeOption, { min: number; max: number }> = {
  next_day: { min: 1, max: 1 },
  two_day: { min: 2, max: 2 },
  two_to_three_day: { min: 2, max: 3 },
  three_to_five_day: { min: 3, max: 5 },
};

/** Ordered list of delivery-time presets (single source for the admin select). */
export const DELIVERY_TIME_OPTIONS = Object.keys(DELIVERY_TIME_TRANSIT) as DeliveryTimeOption[];

/**
 * Safe defaults for a market whose `promise_date` block is missing (e.g. a stale
 * query cache predating this feature). Mirrors the backend PromiseDateConfig
 * defaults; the timezone is derived from the market's first country.
 */
export function defaultPromiseDate(countries: string[]): PromiseDateSettings {
  return {
    active: false,
    cutoff_active: false,
    shipment_days: [1, 2, 3, 4, 5],
    deadline_hour: 12,
    deadline_minute: 0,
    timezone: defaultTimezoneForCountries(countries),
    delivery_time: 'next_day',
    excluded_dates: [],
  };
}

export type PromisePreview = {
  visible: boolean;
  showCountdown: boolean;
  countdownSeconds: number | null;
  /** Promised delivery date as `YYYY-MM-DD`. */
  deliveryDate: string | null;
  /** True when the promised delivery lands on tomorrow's calendar date. */
  isNextDay: boolean;
};

function pad(n: number): string {
  return (n < 10 ? '0' : '') + n;
}

function dateKey(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

type Wall = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

/** Wall-clock fields for `date` as seen in `timeZone` (DST-safe, no offset math). */
function partsInZone(date: Date, timeZone: string): Wall {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const map: Record<string, string> = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }
  let hour = parseInt(map.hour, 10);
  if (hour === 24) hour = 0;
  return {
    year: parseInt(map.year, 10),
    month: parseInt(map.month, 10),
    day: parseInt(map.day, 10),
    hour,
    minute: parseInt(map.minute, 10),
    second: parseInt(map.second, 10),
  };
}

/** ISO-8601 weekday (Mon = 1 … Sun = 7). */
function isoWeekday(year: number, month: number, day: number): number {
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

type Ymd = { year: number; month: number; day: number };

function advanceDays(start: Ymd, steps: number, isExcluded: (key: string) => boolean): Ymd {
  const dt = new Date(Date.UTC(start.year, start.month - 1, start.day));
  let remaining = steps;
  // Safety cap: a config that excludes every upcoming date would never satisfy
  // `remaining`, so bound the scan to ~2 years (mirrors the theme asset).
  let guard = 0;
  while (remaining > 0 && guard < 730) {
    dt.setUTCDate(dt.getUTCDate() + 1);
    guard++;
    if (!isExcluded(dateKey(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate()))) {
      remaining--;
    }
  }
  return { year: dt.getUTCFullYear(), month: dt.getUTCMonth() + 1, day: dt.getUTCDate() };
}

function nextShipmentDay(
  start: Ymd,
  shipmentDays: number[],
  isExcluded: (key: string) => boolean,
): Ymd | null {
  const dt = new Date(Date.UTC(start.year, start.month - 1, start.day));
  for (let i = 0; i < 366; i++) {
    const year = dt.getUTCFullYear();
    const month = dt.getUTCMonth() + 1;
    const day = dt.getUTCDate();
    if (
      shipmentDays.includes(isoWeekday(year, month, day)) &&
      !isExcluded(dateKey(year, month, day))
    ) {
      return { year, month, day };
    }
    dt.setUTCDate(dt.getUTCDate() + 1);
  }
  return null;
}

/**
 * Pure preview of the storefront widget for `settings` at instant `now`. Kept in
 * lock-step with the theme asset (assets/inpost-cutoff-timer.js `computeWidget`).
 */
export function computePromisePreview(settings: PromiseDateSettings, now: Date): PromisePreview {
  const parts = partsInZone(now, settings.timezone || 'UTC');
  const todayKey = dateKey(parts.year, parts.month, parts.day);
  const todayIso = isoWeekday(parts.year, parts.month, parts.day);

  const excluded = settings.excluded_dates ?? [];
  const isExcluded = (key: string) => excluded.includes(key);
  const shipmentDays = settings.shipment_days ?? [];

  const nowSeconds = parts.hour * 3600 + parts.minute * 60 + parts.second;
  const deadlineSeconds = settings.deadline_hour * 3600 + settings.deadline_minute * 60;
  const transitMax = DELIVERY_TIME_TRANSIT[settings.delivery_time]?.max ?? 1;

  let dispatch: Ymd | null;
  let showCountdown = false;
  let countdownSeconds: number | null = null;

  if (settings.cutoff_active) {
    const eligibleToday =
      shipmentDays.includes(todayIso) && !isExcluded(todayKey) && nowSeconds < deadlineSeconds;
    if (!eligibleToday) {
      return {
        visible: false,
        showCountdown: false,
        countdownSeconds: null,
        deliveryDate: null,
        isNextDay: false,
      };
    }
    dispatch = { year: parts.year, month: parts.month, day: parts.day };
    showCountdown = true;
    countdownSeconds = deadlineSeconds - nowSeconds;
  } else {
    dispatch = nextShipmentDay(
      { year: parts.year, month: parts.month, day: parts.day },
      shipmentDays,
      isExcluded,
    );
    if (!dispatch) {
      return {
        visible: false,
        showCountdown: false,
        countdownSeconds: null,
        deliveryDate: null,
        isNextDay: false,
      };
    }
  }

  const delivery = advanceDays(dispatch, transitMax, isExcluded);

  // "next day" = the promised delivery lands on tomorrow's calendar date in the
  // configured timezone (mirrors the theme asset's computeWidget).
  const tomorrow = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  const tomorrowKey = dateKey(
    tomorrow.getUTCFullYear(),
    tomorrow.getUTCMonth() + 1,
    tomorrow.getUTCDate(),
  );
  const deliveryDate = dateKey(delivery.year, delivery.month, delivery.day);

  return {
    visible: true,
    showCountdown,
    countdownSeconds,
    deliveryDate,
    isNextDay: deliveryDate === tomorrowKey,
  };
}

/** Format a countdown in seconds as e.g. "2h 40 min" / "30 min". */
export function formatCountdown(
  totalSeconds: number,
  hoursShort: string,
  minutesShort: string,
): string {
  const minutes = Math.ceil(totalSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}${hoursShort} ${mins} ${minutesShort}`;
  }
  return `${mins} ${minutesShort}`;
}
